import { ColumnDef } from "@tanstack/react-table";
import { Checkbox, Chip } from "@heroui/react";
import { Link } from "@tanstack/react-router";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";
import { BaseField } from "@/types/field";
import { getEnrollmentTierLabel } from "@/lib/utils/enrollment";

export interface FieldColumnOptions {
    growersById: Record<number, Organization>;
    serviceProvidersById: Record<number, Organization>;
    accountManagersById: Record<number, User>;
}

export const createFieldColumns = {
    selection: <T extends BaseField>(): ColumnDef<T> => ({
        id: "selection",
        header: ({ table }) => {
            return (
                <Checkbox
                    className="pl-4"
                    isSelected={table.getIsAllRowsSelected()}
                    onValueChange={(value) => {
                        table.toggleAllRowsSelected(value);
                    }}
                />
            );
        },
        enableSorting: false,
        accessorFn: (row) => row?.id ?? "",
        cell: ({ row }) => {
            return (
                <Checkbox
                    className="pl-4"
                    isSelected={row.getIsSelected()}
                    onValueChange={(value) => row.toggleSelected(value)}
                />
            );
        },
        size: 50,
    }),

    growerAndServiceProvider: <T extends BaseField>(
        options: FieldColumnOptions
    ): ColumnDef<T> => ({
        id: "grower",
        header: "Farm/CSP",
        accessorFn: (row) => row?.grower,
        cell: ({ row }) => {
            const { growersById, serviceProvidersById, accountManagersById } = options;
            return (
                <div className="flex flex-col">
                    <div>{growersById[row.original.grower]?.name}</div>
                    <div className="text-xs text-gray-500">
                        {serviceProvidersById[row.original.service_provider]?.name ?? "N/A"}
                        {" - "}
                        {accountManagersById[row.original.account_manager]?.full_name ?? "N/A"}
                    </div>
                </div>
            );
        },
        size: 250,
    }),

    fieldName: <T extends BaseField & { active_enrollment?: { id: number; enrollment_tier: number; insights_active: boolean } }>(): ColumnDef<T> => ({
        id: "name",
        header: "Field",
        accessorFn: (row) => row?.name ?? "",
        cell: ({ row }) => {
            return (
                <Link
                    to="/dashboard/fields/$fieldId"
                    params={{ fieldId: row.original.id.toString() }}
                    target="_blank"
                >
                    <div className="flex flex-col items-start justify-between w-full">
                        {row.original.name}
                        <div className="flex flex-row gap-1 justify-start">
                            <Chip
                                color={
                                    row.original.active_enrollment
                                        ? "primary"
                                        : "default"
                                }
                                radius="sm"
                                size="sm"
                                classNames={{
                                    base: "text-[0.6rem]",
                                    content: "p-[0.15rem]",
                                }}
                            >
                                {getEnrollmentTierLabel(
                                    row.original.active_enrollment?.enrollment_tier,
                                )}
                            </Chip>
                            {!!row.original.active_enrollment && (
                                <Chip
                                    color={
                                        row.original.active_enrollment.insights_active
                                            ? "secondary"
                                            : "default"
                                    }
                                    radius="sm"
                                    size="sm"
                                    classNames={{
                                        base: "text-[0.6rem]",
                                        content: "p-[0.15rem]",
                                    }}
                                >
                                    {row.original.active_enrollment.insights_active
                                        ? "Insights Active"
                                        : "Insights Inactive"}
                                </Chip>
                            )}
                        </div>
                    </div>
                </Link>
            );
        },
        size: 250,
    }),
};
